import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Loader2, Check, AlertCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/auth";
import { mediaProxyUrlForced } from "@/lib/media";
import Photo360Viewer from "@/components/Photo360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import AiFlagToggle from "@/components/admin/AiFlagToggle";

type Style = { id: number; name: string; description?: string | null };
type JobStatus = {
    id: number;
    status: "pending" | "processing" | "dispatched" | "complete" | "error" | "abort";
    file_url: string | null;
    thumb_url: string | null;
    prompt: string | null;
    style_id: number | null;
    style_name: string | null;
    credit_cost: number | null;
    error_message: string | null;
};
type PairJobItem = { id: number; role: "before" | "after"; prompt: string };
type GeneratedMedia = { media_id: string; stored_url: string; title: string; role?: "before" | "after" };
type Project = { id: string; slug: string; title: string };

const POLL_INTERVAL = 3000;

export default function AdminSkybox() {
    const [styles, setStyles] = useState<Style[]>([]);
    const [prompt, setPrompt] = useState("");
    const [styleId, setStyleId] = useState<number>(67); // M3 Photoreal
    const [generatePair, setGeneratePair] = useState(false);
    const [title, setTitle] = useState("");
    const [projects, setProjects] = useState<Project[]>([]);
    const [assignProjectId, setAssignProjectId] = useState<string>("");

    const [activeJobs, setActiveJobs] = useState<Array<{ job_id: number; role?: "before" | "after"; prompt: string }>>([]);
    const [statuses, setStatuses] = useState<Record<number, JobStatus>>({});
    const [saved, setSaved] = useState<GeneratedMedia[]>([]);
    const [error, setError] = useState<string>("");
    const [busy, setBusy] = useState(false);
    const [creditsSpent, setCreditsSpent] = useState(0);

    const pollTimerRef = useRef<number | null>(null);
    const savingIdsRef = useRef<Set<number>>(new Set());

    useEffect(() => {
        void (async () => {
            const r = await api<{ items: Style[] }>("/api/admin/skybox/styles");
            if (r.ok && r.data) setStyles(r.data.items);
            const p = await api<{ items: Project[] }>("/api/admin/projects");
            if (p.ok && p.data) setProjects(p.data.items || []);
        })();
    }, []);

    // Polling
    useEffect(() => {
        if (activeJobs.length === 0) {
            if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            return;
        }
        const tick = async () => {
            for (const j of activeJobs) {
                const st = statuses[j.job_id];
                if (st?.status === "complete" || st?.status === "error" || st?.status === "abort") {
                    // finalize once
                    if (st.status === "complete" && !savingIdsRef.current.has(j.job_id)) {
                        savingIdsRef.current.add(j.job_id);
                        void finalizeJob(j.job_id, j.role);
                    }
                    continue;
                }
                const r = await api<JobStatus>(`/api/admin/skybox/status/${j.job_id}`);
                if (r.ok && r.data) {
                    setStatuses((cur) => ({ ...cur, [j.job_id]: r.data as JobStatus }));
                }
            }
        };
        pollTimerRef.current = window.setInterval(tick, POLL_INTERVAL) as unknown as number;
        void tick();
        return () => {
            if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        };
    }, [activeJobs, statuses]);

    const finalizeJob = async (jobId: number, role?: "before" | "after") => {
        const r = await api<GeneratedMedia>(`/api/admin/skybox/complete/${jobId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                is_pair_before: role === "before",
                title: title || undefined,
            }),
        });
        if (r.ok && r.data) {
            const g = { ...r.data, role };
            setSaved((s) => [...s, g]);
            setError(r.error || "Speichern fehlgeschlagen.");
        }
    };

    const start = async () => {
        setError("");
        setSaved([]);
        setStatuses({});
        savingIdsRef.current.clear();
        if (!prompt.trim()) {
            setError("Bitte einen Prompt eingeben.");
            return;
        }
        setBusy(true);
        const r = await api<{
            job_id?: number;
            pair_job_ids?: PairJobItem[];
            credit_cost?: number;
        }>("/api/admin/skybox/generate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                prompt,
                style_id: styleId,
                generate_pair: generatePair,
                title: title || undefined,
            }),
        });
        setBusy(false);
        if (!r.ok || !r.data) {
            setError(r.error || "Generierung fehlgeschlagen.");
            return;
        }
        const data = r.data;
        setCreditsSpent((c) => c + (data.credit_cost || 0) + (generatePair ? 2 : 1));
        if (data.pair_job_ids) {
            setActiveJobs(data.pair_job_ids.map((p) => ({ job_id: p.id, role: p.role, prompt: p.prompt })));
        } else if (data.job_id) {
            setActiveJobs([{ job_id: data.job_id, prompt }]);
        }
    };

    const reset = () => {
        setActiveJobs([]);
        setStatuses({});
        setSaved([]);
        setError("");
        savingIdsRef.current.clear();
    };

    const attachToProject = async (mediaId: string) => {
        if (!assignProjectId) {
            window.alert("Bitte oben ein Projekt aus dem Dropdown wählen.");
            return;
        }
        const proj = await api<{ media_ids: string[] }>(`/api/admin/projects/${assignProjectId}`);
        if (!proj.ok || !proj.data) return;
        const newIds = Array.from(new Set([...(proj.data.media_ids || []), mediaId]));
        await api(`/api/admin/projects/${assignProjectId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ media_ids: newIds }),
        });
        window.alert("Zum Projekt hinzugefügt.");
    };

    const attachPair = async () => {
        if (!assignProjectId) {
            window.alert("Bitte oben ein Projekt wählen.");
            return;
        }
        const before = saved.find((s) => s.role === "before");
        const after = saved.find((s) => s.role === "after");
        if (!before || !after) {
            window.alert("Es sind nicht beide Bilder eines Paares vorhanden.");
            return;
        }
        // Ensure both media are in media_ids
        const proj = await api<{ media_ids: string[] }>(`/api/admin/projects/${assignProjectId}`);
        const newIds = Array.from(new Set([...((proj.data?.media_ids) || []), before.media_id, after.media_id]));
        await api(`/api/admin/projects/${assignProjectId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ media_ids: newIds }),
        });
        // Create pair
        const r = await api<{ pair: { id: string } }>(`/api/admin/projects/${assignProjectId}/pairs`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                before_media_id: before.media_id,
                after_media_id: after.media_id,
                title: title || (before.title || "").replace(/\s*\(Vorher\)$/, "").replace(/^Skybox\s*–\s*/, "") || null,
            }),
        });
        if (r.ok) {
            window.alert("Vorher/Nachher-Paar im Projekt gespeichert.");
        } else {
            window.alert("Fehler beim Erstellen des Paares: " + (r.error || ""));
        }
    };

    const allComplete = useMemo(
        () => activeJobs.length > 0 && activeJobs.every((j) => statuses[j.job_id]?.status === "complete"),
        [activeJobs, statuses],
    );
    const anyError = useMemo(
        () => activeJobs.some((j) => {
            const s = statuses[j.job_id]?.status;
            return s === "error" || s === "abort";
        }),
        [activeJobs, statuses],
    );

    return (
        <div className="max-w-4xl mx-auto py-10 px-4">
            <div className="flex items-center gap-3 mb-2">
                <Sparkles size={22} style={{ color: "hsl(var(--hf-gold))" }} />
                <div className="hf-eyebrow">Skybox 360°</div>
            </div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="admin-skybox-title">
                Skybox 360° · KI-Generierung
            </h1>
            <p className="mt-3 text-hf-muted text-sm max-w-2xl">
                Generieren Sie photorealistische 360°-Panoramen per KI. Optional als Vorher/Nachher-Paar für den Slider.
            </p>

            {/* Formular */}
            <div
                className="mt-8 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <label className="block text-xs tracking-[0.18em] uppercase text-hf-muted mb-1.5">Prompt</label>
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={3}
                    placeholder="modern bright loft living room, floor-to-ceiling windows, warm afternoon sunlight, minimalist white and pale oak"
                    className="w-full px-3 py-2 border rounded-sm text-sm bg-white"
                    style={{ borderColor: "hsl(var(--hf-line))" }}
                    disabled={busy || activeJobs.length > 0}
                    data-testid="skybox-prompt"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                        <label className="block text-xs tracking-[0.18em] uppercase text-hf-muted mb-1.5">Stil</label>
                        <select
                            value={styleId}
                            onChange={(e) => setStyleId(Number(e.target.value))}
                            className="w-full px-3 py-2 border rounded-sm bg-white text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            disabled={busy || activeJobs.length > 0}
                            data-testid="skybox-style"
                        >
                            {styles.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs tracking-[0.18em] uppercase text-hf-muted mb-1.5">Titel (optional)</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="wird aus dem Prompt abgeleitet"
                            className="w-full px-3 py-2 border rounded-sm bg-white text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="skybox-title"
                        />
                    </div>
                    <div>
                        <label className="block text-xs tracking-[0.18em] uppercase text-hf-muted mb-1.5">Zu Projekt hinzufügen</label>
                        <select
                            value={assignProjectId}
                            onChange={(e) => setAssignProjectId(e.target.value)}
                            className="w-full px-3 py-2 border rounded-sm bg-white text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="skybox-assign-project"
                        >
                            <option value="">— nur speichern —</option>
                            {projects.map((p) => (
                                <option key={p.id} value={p.id}>{p.title}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={generatePair}
                        onChange={(e) => setGeneratePair(e.target.checked)}
                        disabled={busy || activeJobs.length > 0}
                        data-testid="skybox-pair"
                    />
                    Vorher/Nachher-Paar generieren (zusätzliche Rohbau-Version)
                </label>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={() => void start()}
                        disabled={busy || activeJobs.length > 0 || !prompt.trim()}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-50"
                        style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}
                        data-testid="skybox-generate-btn"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        Generieren
                    </button>
                    {activeJobs.length > 0 && (
                        <button
                            type="button"
                            onClick={reset}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs"
                            style={{ border: "1px solid hsl(var(--hf-line))" }}
                            data-testid="skybox-reset-btn"
                        >
                            <RefreshCw size={12} /> Neu starten
                        </button>
                    )}
                    <span className="text-xs text-hf-muted ml-auto">Credits diese Session: <strong>{creditsSpent}</strong></span>
                </div>
                {error && (
                    <div className="mt-4 flex items-start gap-2 text-sm" style={{ color: "hsl(var(--hf-danger))" }} data-testid="skybox-error">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {/* Job-Status */}
            {activeJobs.length > 0 && (
                <div className="mt-8" data-testid="skybox-status-panel">
                    <div className="hf-eyebrow">Generierung läuft</div>
                    <p className="mt-2 text-sm text-hf-muted">
                        Bitte warten — Blockade Labs benötigt in der Regel <strong>1–2 Minuten</strong> pro Panorama.
                        Nicht die Seite verlassen.
                    </p>
                    <div className="mt-4 space-y-2">
                        {activeJobs.map((j) => {
                            const st = statuses[j.job_id];
                            const done = st?.status === "complete";
                            const err = st?.status === "error" || st?.status === "abort";
                            return (
                                <div
                                    key={j.job_id}
                                    className="flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm"
                                    style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                                    data-testid={`skybox-job-${j.job_id}`}
                                >
                                    {done ? (
                                        <Check size={16} style={{ color: "hsl(var(--hf-gold-dark))" }} />
                                    ) : err ? (
                                        <AlertCircle size={16} style={{ color: "hsl(var(--hf-danger))" }} />
                                    ) : (
                                        <span className="inline-flex gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "hsl(var(--hf-gold))", animationDelay: "0s" }} />
                                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "hsl(var(--hf-gold))", animationDelay: "0.2s" }} />
                                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "hsl(var(--hf-gold))", animationDelay: "0.4s" }} />
                                        </span>
                                    )}
                                    <span className="font-mono text-[10px] text-hf-muted">#{j.job_id}</span>
                                    {j.role && (
                                        <span className="text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                                            style={{ background: "hsl(var(--hf-ink) / 0.06)" }}>
                                            {j.role === "before" ? "Vorher" : "Nachher"}
                                        </span>
                                    )}
                                    <span className="truncate flex-1">{st?.prompt || j.prompt}</span>
                                    <span className="text-xs text-hf-muted">{st?.status || "pending"}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Ergebnisse */}
            {saved.length > 0 && (
                <div className="mt-10" data-testid="skybox-results">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                            <div className="hf-eyebrow">Fertig</div>
                            <h2 className="font-display text-2xl mt-1">
                                {saved.length === 1 ? "1 Panorama" : `${saved.length} Panoramen`} gespeichert
                            </h2>
                        </div>
                        {saved.length === 2 && (
                            <button
                                type="button"
                                onClick={() => void attachPair()}
                                disabled={!assignProjectId}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm disabled:opacity-50"
                                style={{ background: "hsl(var(--hf-gold))", color: "hsl(var(--hf-ink))" }}
                                data-testid="skybox-save-pair-btn"
                            >
                                Als Vorher/Nachher-Paar im Projekt speichern
                            </button>
                        )}
                    </div>
                    <div className={`grid gap-5 mt-5 ${saved.length === 2 ? "md:grid-cols-2" : "grid-cols-1"}`}>
                        {saved.map((m) => (
                            <div
                                key={m.media_id}
                                className="p-3 rounded-sm"
                                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                                data-testid={`skybox-media-${m.media_id}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-display truncate mr-2">{m.title}</span>
                                    {m.role && (
                                        <span className="text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                                            style={{ background: "hsl(var(--hf-gold-soft))", color: "hsl(var(--hf-ink))" }}>
                                            {m.role === "before" ? "Vorher" : "Nachher"}
                                        </span>
                                    )}
                                </div>
                                <div className="aspect-[2/1] rounded-sm overflow-hidden" style={{ background: "hsl(var(--hf-ink))" }}>
                                    <ViewerErrorBoundary>
                                        <Photo360Viewer src={mediaProxyUrlForced(m.media_id)} />
                                    </ViewerErrorBoundary>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2 items-center">
                                    <button
                                        type="button"
                                        onClick={() => void attachToProject(m.media_id)}
                                        disabled={!assignProjectId}
                                        className="px-3 py-1.5 rounded-sm text-xs disabled:opacity-50"
                                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                                        data-testid={`skybox-attach-${m.media_id}`}
                                    >
                                        Als reguläres Media zum Projekt hinzufügen
                                    </button>
                                    <AiFlagToggle
                                        mediaId={m.media_id}
                                        compact
                                        testIdPrefix={`skybox-ai-flag-${m.media_id}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {(allComplete || anyError) && (
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={reset}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm"
                                style={{ border: "1px solid hsl(var(--hf-line))" }}
                                data-testid="skybox-restart-btn"
                            >
                                <RefreshCw size={14} /> Neue Generierung starten
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
